import { Injectable, signal, computed } from '@angular/core';
import { LogLayer, ConsoleTransport, ILogLayer, LogLevelType } from 'loglayer';
import type { LogLayerPlugin, PluginBeforeMessageOutParams } from 'loglayer';
import { environment } from 'src/environments/environment';

export const LOCAL_STORAGE_KEY = 'LOGGING_FLAGS';

const START_TIME = Date.now();

type LoggerState = {
  name?: string;
  enabled: boolean;
  logger: ILogLayer;
};

type LoggingMethods = {
  debug(...args: any[]): void;
  warn(...args: any[]): void;
  log(...args: any[]): void;
  info(...args: any[]): void;
  error(...args: any[]): void;
};

export type Logger = Omit<LogLayer, 'info' | 'warn' | 'error' | 'debug'> &
  LoggingMethods;

type LoggerFactory = (name?: string, level?: LogLevelType) => Logger;

const modulePluginFactory = (moduleName: string, id): LogLayerPlugin => {
  const plugin: LogLayerPlugin = {
    id: 'module',
    onBeforeMessageOut: ({ messages }: PluginBeforeMessageOutParams) => {
      return messages.map((msg, index) => {
        if (index !== 0 || typeof msg !== 'string') {
          return msg;
        }
        return `[${moduleName}][${id}] ${msg}`;
      });
    },
  };

  return plugin;
};

/**
 * Pass-through plugin: lets the console natively render objects
 * (expandable/inspectable in devtools) instead of eagerly stringifying them.
 * This is both faster (no JSON.stringify) and gives better DX in the console.
 */
const stringifyPlugin: LogLayerPlugin = {
  id: 'auto-stringify',
  onBeforeMessageOut: ({ messages }: PluginBeforeMessageOutParams) => {
    return messages;
  },
};

const timerPlugin: LogLayerPlugin = {
  id: 'timestamp',
  onBeforeMessageOut: ({ messages }: PluginBeforeMessageOutParams) => {
    const timestamp = (Date.now() - START_TIME) / 1000;
    return messages.map((msg, index) =>
      index === 0 ? `[${timestamp.toFixed(1)}s] ${msg}` : msg
    );
  },
};

// Cache transports by log level to avoid creating duplicate instances
const transportCache = new Map<LogLevelType, ConsoleTransport>();

function getTransport(level: LogLevelType): ConsoleTransport {
  let transport = transportCache.get(level);
  if (!transport) {
    transport = new ConsoleTransport({ logger: console, level });
    transportCache.set(level, transport);
  }
  return transport;
}

const defaultLoggerFactory: LoggerFactory = (
  name,
  level: LogLevelType = 'error' as LogLevelType
) => {
  const transport = getTransport(level);
  // convert the LogLayer instance to match the Logger interface, while keeping the original type for internal use
  // This allows the plugin to work it's magic without having typescript issues.
  const logger = new LogLayer({
    prefix: `[${name}]  `,
    transport,
    plugins: [stringifyPlugin],
  }) as unknown as Logger;
  return logger;
};

const timedLoggerFactory: LoggerFactory = (
  name,
  level: LogLevelType = 'error' as LogLevelType
) => {
  const transport = getTransport(level);
  // convert the LogLayer instance to match the Logger interface, while keeping the original type for internal use
  // This allows the plugin to work it's magic without having typescript issues.
  const logger = new LogLayer({
    prefix: `[${name}]  `,
    transport,
    plugins: [stringifyPlugin, timerPlugin],
  }) as unknown as Logger;
  return logger;
};

function getModuleLoggerFactory(moduleName, id): LoggerFactory {
  const plugin = modulePluginFactory(moduleName, id);
  return function (_name, level): Logger {
    const transport = getTransport(level);
    const logger = new LogLayer({
      transport,
      plugins: [stringifyPlugin, plugin],
    }) as unknown as Logger;
    return logger;
  };
}

const LOGGERS = new Map<string, Logger>();
const STATE = signal(new Map());

// Cache parsed localStorage to avoid repeated reads/parses
let loggingFlagsCache: Record<string, Record<string, boolean>> | null = null;

function getLoggingFlags(): Record<string, Record<string, boolean>> {
  if (loggingFlagsCache === null) {
    const storedString = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    loggingFlagsCache = storedString ? JSON.parse(storedString) : {};
  }
  return loggingFlagsCache;
}

function setLoggingFlags(flags: Record<string, Record<string, boolean>>): void {
  loggingFlagsCache = flags;
  window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(flags));
}

/**
 * Patches a logger so that .error() always outputs even when logging is disabled.
 * Preserves the previous enabled state so that re-enabling a logger isn't undone
 * by the patch after the next .error() call.
 *
 * Uses ORIGINAL_ERROR to guard against double-patching and to store the true
 * original error function for clean restoration via unpatchError.
 */
const ORIGINAL_ERROR = new WeakMap<Logger, (...args: any[]) => void>();

function patchError(logger: Logger, loggerName: string): void {
  if (ORIGINAL_ERROR.has(logger)) {
    return; // already patched – nothing to do
  }
  const originalError = logger.error.bind(logger);
  ORIGINAL_ERROR.set(logger, originalError);
  logger.error = (...args: any[]) => {
    const wasEnabled = STATE()?.get(loggerName)?.enabled ?? false;
    logger.enableLogging();
    originalError(...args);
    if (!wasEnabled) {
      logger.disableLogging();
    }
  };
}

function unpatchError(logger: Logger): void {
  const originalError = ORIGINAL_ERROR.get(logger);
  if (originalError) {
    logger.error = originalError;
    ORIGINAL_ERROR.delete(logger);
  }
}

// TODO: Implement log levels etc using logtape https://logtape.org/
@Injectable({
  providedIn: 'root',
})
export class LogService implements LoggingMethods {
  constructor() {}

  getLogger(
    name?: string,
    defaultEnabled = true,
    factory = defaultLoggerFactory
  ): Logger {
    // Auto-detect caller's class name from stack trace if name not provided
    const loggerName = name ?? this.getCallerName();

    // Fast path: return cached logger immediately if it already exists
    const cached = LOGGERS.get(loggerName);
    if (cached) {
      return cached;
    }

    // Determine enabled state from localStorage (only on first creation)
    let enabled = defaultEnabled;

    if (name) {
      const stored = getLoggingFlags();
      const envKey = environment.platform.toLowerCase();
      const storedEnv = stored[envKey] || {};
      if (storedEnv[name] === undefined) {
        stored[envKey] = { ...storedEnv, [name]: defaultEnabled };
        setLoggingFlags(stored);
      } else {
        enabled = storedEnv[name];
      }
    }

    // Construct a new logger
    const level = environment.logLevel || ('trace' as LogLevelType);
    const logger = factory(loggerName, level);
    LOGGERS.set(loggerName, logger);

    if (!enabled) {
      logger.disableLogging();
      patchError(logger, loggerName);
    }

    // Only update signal state for named loggers (used by admin UI)
    if (name) {
      const s = STATE();
      s.set(name, { name, enabled });
      STATE.set(s);
    }

    return logger;
  }

  getTimedLogger(
    name?: string,
    defaultEnabled = true,
    factory = timedLoggerFactory
  ): Logger {
    return this.getLogger(name, defaultEnabled, factory);
  }

  getModuleLogger(
    moduleName: string,
    id: string,
    defaultEnabled = true
  ): Logger {
    const factory = getModuleLoggerFactory(moduleName, id);
    const key = `${moduleName}::${id}`;
    return this.getLogger(key, defaultEnabled, factory);
  }

  // Used the by the admin page to toggle loggers on/off, which updates both the logger state and the signal state for UI display
  setLoggerState(name: string, enabled: boolean) {
    const state = STATE().get(name);

    const logger = LOGGERS.get(name);
    if (logger) {
      if (enabled) {
        unpatchError(logger);
        logger.enableLogging();
      } else {
        logger.disableLogging();
        patchError(logger, name);
      }
    }

    const newState = { ...state, enabled };
    const s = STATE().set(name, newState);
    STATE.set(s);
  }

  // This is used by the admin page to display all loggers and toggle them on/off
  loggers = computed(() => {
    const state = STATE();
    return state;
  });

  /**
   * Extracts the caller's class name from the stack trace
   * @private
   */
  private getCallerName(): string {
    try {
      const stackLine = new Error().stack?.split('\n')[3]; // Get caller's caller (skip Error, getCallerName, getLogger)
      if (!stackLine) {
        return 'Anonymous';
      }

      // Try to match class.method pattern: "at ClassName.methodName" or "at new ClassName"
      const classMatch = stackLine.match(/at\s+(?:new\s+)?(\w+)[.\s]/);
      if (classMatch && classMatch[1] !== 'Object') {
        return classMatch[1];
      }

      // Fallback: try to extract filename without extension
      const fileMatch = stackLine.match(/([^/\\]+)\.ts/);
      if (fileMatch) {
        return fileMatch[1];
      }

      return 'Anonymous';
    } catch (e) {
      return 'Anonymous';
    }
  }

  debug(...args): void {
    console.debug(...args);
  }

  warn(...args): void {
    console.warn(...args);
  }

  log(...args): void {
    console.log(...args);
  }

  info(...args: any[]): void {
    console.info(...args);
  }

  error(...args: any[]): void {
    console.error(...args);
  }
}
